import React, { useState } from 'react';

const ACCENT = '#10B981';
const CYAN = '#06B6D4';
const AMBER = '#F59E0B';

const CARD: React.CSSProperties = {
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: 20,
  padding: 28,
};

function shortenHash(h: string) {
  const s = h.trim();
  if (s.length <= 14) return s;
  return `${s.slice(0, 6)}...${s.slice(-4)}`;
}

export function BlockchainTrustPage() {
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [certInput, setCertInput] = useState('');

  const onVerify = () => {
    if (!certInput.trim()) return;
    setVerified(false);
    setVerifying(true);
    window.setTimeout(() => {
      setVerified(true);
      setVerifying(false);
    }, 2000);
  };

  const txDisplay = certInput.trim() || '0x7f3a9b2c8d1e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9';
  const polygonUrl = `https://polygonscan.com/tx/${encodeURIComponent(txDisplay)}`;

  return (
    <div className="odc-page relative z-10" style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <style>{`
        .odc-bc-types { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
        @media (max-width: 720px) { .odc-bc-types { grid-template-columns: 1fr !important; } }
        .odc-bc-steps { display: flex; align-items: flex-start; justify-content: center; gap: 12px; flex-wrap: wrap; }
        .odc-bc-why { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 20px; }
        @media (max-width: 640px) { .odc-bc-why { grid-template-columns: 1fr !important; } }
      `}</style>

      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 280px', minWidth: 0 }}>
          <span
            style={{
              display: 'inline-flex',
              padding: '5px 14px',
              borderRadius: 999,
              border: '1px solid rgba(6,182,212,0.35)',
              background: 'rgba(6,182,212,0.1)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.14em',
              color: CYAN,
              textTransform: 'uppercase',
            }}
          >
            BLOCKCHAIN TRUST · BETA
          </span>
          <h1 style={{ margin: '12px 0 0', fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 800, color: 'var(--text)' }}>
            Verified Trust Certificates
          </h1>
          <p style={{ margin: '12px 0 0', color: 'var(--muted)', fontSize: 15, maxWidth: 560, lineHeight: 1.6 }}>
            Blockchain-powered proof of your business reputation. Tamper-proof. Forever verifiable.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {['🔗 Polygon Blockchain', '⚡ Instant Verify', '🛡️ Tamper-Proof'].map((t) => (
            <span
              key={t}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 999,
                padding: '8px 14px',
                background: 'var(--card)',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text)',
              }}
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      <div className="odc-card" style={{ ...CARD, transform: 'none' }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>Why Blockchain Certificates?</h2>
        <div className="odc-bc-why">
          <div style={{ minWidth: 0 }}>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14, color: 'var(--muted)', lineHeight: 1.55 }}>
              <li>❌ Fake reviews — anyone can post</li>
              <li>❌ Edited screenshots — easily faked</li>
              <li>❌ Unverifiable claims — no proof</li>
              <li>❌ Reviews can be deleted — lost forever</li>
            </ul>
          </div>
          <div style={{ minWidth: 0 }}>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14, color: ACCENT, lineHeight: 1.55, fontWeight: 600 }}>
              <li>✅ Blockchain reviews — immutable forever</li>
              <li>✅ Cryptographic proof — cannot be faked</li>
              <li>✅ QR code verify — anyone can scan &amp; check</li>
              <li>✅ On-chain storage — never deleted</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="odc-card" style={{ ...CARD, transform: 'none' }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>Your Trust Certificates</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 20 }}>
          {[
            {
              icon: '🏆',
              title: 'Business Verified Certificate',
              sub: 'ABC Real Estate',
              issued: 'Issued: April 2026',
              hash: '0x7f3a...9b2c',
              extra: null as string | null,
            },
            {
              icon: '⭐',
              title: '5-Star Service Certificate',
              sub: null,
              issued: null,
              hash: '0x2d8f...4e1a',
              extra: '125 verified reviews on blockchain',
            },
            {
              icon: '🎯',
              title: '100+ Leads Certificate',
              sub: null,
              issued: null,
              hash: '0x9c4b...7f3d',
              extra: 'Performance verified by ODC AI',
            },
          ].map((c) => (
            <div
              key={c.title}
              style={{
                border: '1px solid rgba(16,185,129,0.3)',
                borderRadius: 16,
                padding: 20,
                background: 'rgba(16,185,129,0.03)',
                position: 'relative',
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
                <div style={{ fontSize: 36 }}>{c.icon}</div>
                <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>{c.title}</h3>
                  {c.sub ? <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--text)' }}>{c.sub}</p> : null}
                  {c.issued ? <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--muted)' }}>{c.issued}</p> : null}
                  {c.extra ? <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--muted)' }}>{c.extra}</p> : null}
                  <p style={{ margin: '10px 0 0', fontFamily: 'ui-monospace, monospace', fontSize: 11, color: 'var(--muted)' }}>Hash: {c.hash}</p>
                </div>
                <div
                  style={{
                    width: 60,
                    height: 60,
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    color: 'var(--muted)',
                    flexShrink: 0,
                  }}
                >
                  QR
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
                <span className="odc-badge odc-badge-green" style={{ fontSize: 10 }}>
                  Verified
                </span>
                <button type="button" className="odc-btn-ghost" style={{ fontSize: 12, padding: '6px 12px' }}>
                  Share Certificate
                </button>
              </div>
              <span
                style={{
                  position: 'absolute',
                  bottom: 12,
                  right: 14,
                  fontSize: 10,
                  opacity: 0.15,
                  fontWeight: 700,
                  pointerEvents: 'none',
                }}
              >
                Verified by ODC
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="odc-card" style={{ ...CARD, transform: 'none' }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>🔍 Verify Any Certificate</h2>
        <p style={{ margin: '8px 0 0', color: 'var(--muted)', fontSize: 14 }}>Enter certificate hash to verify authenticity</p>
        <input
          className="odc-input"
          style={{ width: '100%', marginTop: 16, maxWidth: 480 }}
          placeholder="Enter certificate hash e.g. 0x7f3a..."
          value={certInput}
          onChange={(e) => {
            setCertInput(e.target.value);
            setVerified(false);
          }}
        />
        <button type="button" className="odc-btn-primary" style={{ marginTop: 14 }} onClick={onVerify} disabled={verifying}>
          {verifying ? 'Verifying on blockchain... ⏳' : 'Verify Certificate'}
        </button>
        {verified ? (
          <div
            style={{
              marginTop: 20,
              border: `1px solid ${ACCENT}55`,
              borderRadius: 16,
              padding: 20,
              background: 'rgba(16,185,129,0.08)',
            }}
          >
            <p style={{ margin: 0, fontWeight: 800, fontSize: 16, color: ACCENT }}>✅ Certificate Verified!</p>
            <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--text)' }}>
              This certificate is authentic and has not been tampered with.
            </p>
            <ul style={{ margin: '14px 0 0', padding: 0, listStyle: 'none', fontSize: 13, color: 'var(--muted)', lineHeight: 1.8 }}>
              <li>Issued to: ABC Real Estate</li>
              <li>Issue Date: April 13, 2026</li>
              <li>Block Number: #47,293,847</li>
              <li>Network: Polygon (MATIC)</li>
              <li style={{ fontFamily: 'ui-monospace, monospace' }}>Transaction: {shortenHash(txDisplay)}</li>
            </ul>
            <a
              href={polygonUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="odc-btn-ghost"
              style={{ marginTop: 14, display: 'inline-flex', textDecoration: 'none' }}
            >
              View on PolygonScan →
            </a>
          </div>
        ) : null}
      </div>

      <div>
        <h2 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 800, color: 'var(--text)', textAlign: 'center' }}>
          How Blockchain Certificates Work
        </h2>
        <div className="odc-bc-steps">
          {(
            [
              { icon: '📝', title: 'You Earn It', body: 'Complete 10 verified reviews or 100 leads in ODC' },
              { icon: '🔗', title: 'ODC Records It', body: 'Certificate minted on Polygon blockchain — immutable' },
              { icon: '📱', title: 'Share It', body: 'QR code + link — share on website, WhatsApp, social' },
              { icon: '✅', title: 'Anyone Verifies', body: 'Customer scans QR → blockchain confirms → instant trust' },
            ] as const
          ).map((s, i, arr) => (
            <React.Fragment key={s.title}>
              <div style={{ flex: '1 1 160px', maxWidth: 200, textAlign: 'center' }}>
                <div style={{ fontSize: 28 }}>{s.icon}</div>
                <p style={{ margin: '8px 0 0', fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{s.title}</p>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>{s.body}</p>
              </div>
              {i < arr.length - 1 ? (
                <span style={{ fontSize: 20, color: 'var(--muted)', alignSelf: 'center', padding: '0 4px' }} aria-hidden>
                  →
                </span>
              ) : null}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div>
        <h2 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>Available Certificates</h2>
        <div className="odc-bc-types">
          {[
            {
              icon: '🏆',
              title: 'Business Verified',
              req: 'Complete ODC onboarding + 1 month active',
              val: 'Proves legitimate business',
              price: 'Free with all plans',
              ok: true,
            },
            {
              icon: '⭐',
              title: 'Review Champion',
              req: '50+ verified 5-star reviews',
              val: 'Proves excellent service',
              price: 'Free with Pro+',
              ok: true,
            },
            {
              icon: '🎯',
              title: 'Growth Certified',
              req: '100+ leads in 30 days via ODC',
              val: 'Proves marketing results',
              price: 'Free with Pro+',
              ok: false,
            },
            {
              icon: '💰',
              title: 'Revenue Verified',
              req: '₹10 Lakh+ revenue tracked via ODC',
              val: 'Proves financial performance',
              price: 'Enterprise only',
              ok: false,
            },
          ].map((x) => (
            <div key={x.title} className="odc-card" style={{ ...CARD, padding: 22, transform: 'none' }}>
              <div style={{ fontSize: 28 }}>{x.icon}</div>
              <h3 style={{ margin: '10px 0 0', fontWeight: 800, fontSize: 16, color: 'var(--text)' }}>{x.title}</h3>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
                <strong style={{ color: 'var(--text)' }}>Requirements:</strong> {x.req}
              </p>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
                <strong style={{ color: 'var(--text)' }}>Value:</strong> {x.val}
              </p>
              <p style={{ margin: '8px 0 0', fontSize: 13, fontWeight: 700, color: ACCENT }}>{x.price}</p>
              <span
                className="odc-badge"
                style={{
                  marginTop: 12,
                  display: 'inline-block',
                  fontSize: 10,
                  background: x.ok ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)',
                  color: x.ok ? ACCENT : AMBER,
                  border: `1px solid ${x.ok ? `${ACCENT}44` : `${AMBER}44`}`,
                }}
              >
                {x.ok ? 'Available' : 'Coming Soon'}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div
        className="odc-card"
        style={{
          ...CARD,
          transform: 'none',
          textAlign: 'center',
          border: '2px solid transparent',
          background: 'linear-gradient(var(--card), var(--card)) padding-box, linear-gradient(135deg, rgba(6,182,212,0.45), rgba(6,182,212,0.12)) border-box',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>Build Trust That Cannot Be Faked</h3>
        <p style={{ margin: '12px 0 0', color: 'var(--muted)', fontSize: 15 }}>Start earning blockchain certificates today</p>
        <button type="button" className="odc-btn-primary" style={{ marginTop: 18 }}>
          Enable Blockchain Trust
        </button>
        <p style={{ margin: '14px 0 0', fontSize: 12, color: 'var(--muted)' }}>
          Powered by Polygon blockchain. Gas fees covered by ODC.
        </p>
      </div>
    </div>
  );
}
